<script lang="ts">
  import { untrack } from 'svelte';
  import { nav } from './lib/nav.svelte';
  import { subjectKey } from './lib/subject';
  import { session } from './lib/state/session.svelte';
  import { MOCK } from './lib/mock/flag';
  import { trustImageHost } from './lib/logic/markdown';
  import Rail from './lib/components/Rail.svelte';
  import SignIn from './views/SignIn.svelte';
  import Dashboard from './views/Dashboard.svelte';
  import RepoPage from './views/RepoPage.svelte';
  import RepoIndex from './views/RepoIndex.svelte';
  import PrView from './views/PrView.svelte';
  import CompareView from './views/CompareView.svelte';
  import RefView from './views/RefView.svelte';
  import PaneBoundary from './lib/components/PaneBoundary.svelte';
  import LoadingGate from './lib/loading/LoadingGate.svelte';
  import { oneStep } from './lib/loading/steps';
  import OrgSettings from './views/OrgSettings.svelte';
  import Feedback from './views/Feedback.svelte';
  import Settings from './views/Settings.svelte';
  import { settings } from './lib/state/settings.svelte';
  import FeedbackDialog from './lib/components/FeedbackDialog.svelte';
  import RelayNotices from './lib/components/RelayNotices.svelte';
  import { feedbackUi } from './lib/feedback/feedback.svelte';
  import { jumpUi, places } from './lib/jump/jump.svelte';
  import { plugSource } from './lib/jump/github';
  import { repositoriesSource } from './lib/jump/sources';
  import { repoIndex } from './lib/state/repos.svelte';
  import JumpBar from './lib/components/JumpBar.svelte';
  import { carryOut, keyboard, useKeys } from './lib/keys/keyboard.svelte';
  import { updateApp } from './lib/commands/page';
  import CheckingAccess from './lib/components/CheckingAccess.svelte';
  import OperatorFooter from './lib/components/OperatorFooter.svelte';
  import Unreachable from './lib/components/Unreachable.svelte';
  import KeyGuide from './lib/components/KeyGuide.svelte';
  import ToastRegion from './lib/components/ToastRegion.svelte';
  import KeyHint from './lib/components/KeyHint.svelte';
  import EarlyAccess from './lib/components/EarlyAccess.svelte';
  import { FEEDBACK_PARAM } from './lib/earlyAccess';
  import { build } from './lib/api/version.svelte';

  void session.load();

  const route = $derived(nav.route);

  $effect.pre(() => trustImageHost(session.config?.github_web_url));

  // The new-build banner's height, which the local-agent dock sits below (RelayNotices).
  let newBuildH = $state(0);
  $effect(() => {
    const h = build.stale ? newBuildH : 0;
    if (h) document.documentElement.style.setProperty('--new-build-h', `${h + 8}px`);
    else document.documentElement.style.removeProperty('--new-build-h');
  });

  // Said once when the app comes back after the "can't reach" page.
  let recovered = $state('');
  let wasDown = false;
  $effect(() => {
    const down = session.status === 'error';
    if (wasDown && session.status === 'ready') recovered = 'Changeling is back.';
    wasDown = down;
  });

  // The app's own keys, under every page's: a page that binds the key takes it first.
  useKeys({
    scope: 'app',
    handle: (command) =>
      carryOut(updateApp({ signedIn: !!session.user, inSettings: nav.route.name === 'settings', route: nav.route }, command)[1], (effect) => {
        if (effect.kind === 'show-feedback') feedbackUi.show();
        else if (effect.kind === 'show-jump') jumpUi.show();
        else if (effect.kind === 'go') nav.go(effect.path);
        else nav.go('/settings');
      }),
  });

  // A link from outside the app (the extension's ribbon) asks for the feedback form: it opens once, as its key
  // would open it, and the request leaves the URL.
  $effect(() => {
    if (!session.user || !nav.query.has(FEEDBACK_PARAM)) return;
    nav.setQuery({ [FEEDBACK_PARAM]: null }, { replace: true });
    keyboard.run({ kind: 'open', menu: 'feedback' });
  });

  // The repository index's repositories, in the jump bar: the index is made, and its listing read, only once the bar is
  // asked for a text a repository could match (jump/sources.ts `repositoriesSource`).
  plugSource('repositories', repositoriesSource(repoIndex));

  // The subject a page shows is the jump bar's latest recent: a pull request's and a compare's as the page opens (a
  // history's once its page has resolved it, views/RefPage.svelte).
  $effect(() => {
    const r = nav.route;
    if (session.user && 'subject' in r) untrack(() => places.visit({ key: subjectKey(r.subject) }));
  });

  // The signed-in user's settings are read once their account is the page's (a new account resets them).
  $effect(() => {
    if (session.status === 'ready' && session.user) settings.open();
  });

  // Back in view after a while: the server may have been deployed meanwhile.
  function onVisible() {
    if (document.visibilityState === 'visible') void build.check();
  }
  // Looked at again: a setting may have been changed on another device.
  function onFocus() {
    if (session.user) settings.refresh();
  }
</script>

<svelte:document onvisibilitychange={onVisible} />
<svelte:window onfocus={onFocus} />

{#if build.stale}
  <div class="new-build" role="status" bind:offsetHeight={newBuildH}>
    <span>A new version of Changeling is out. Reload to keep saving comments; your unsent text is kept.</span>
    <button class="btn small" onclick={() => location.reload()}>Reload</button>
  </div>
{/if}

{#if MOCK}<div class="sr-only" aria-live="polite">Mock mode</div>{/if}
<div class="sr-only" role="status">{recovered}</div>
<CheckingAccess />
{#if import.meta.env.DEV && !MOCK}
  <!-- Local development: what the page waits on while the API server is being built. -->
  {#await import('./dev/DevBackend.svelte') then dev}<dev.default />{/await}
{/if}
<KeyGuide />
<KeyHint />
<!-- The toast, for every page (a pull request's failed writes, a refused copy, a copied link). -->
<ToastRegion />

<EarlyAccess />
<div class="page">
  <!-- Signed in, the view has a loading gate of its own, which takes this one's screen over. The account last signed
       in here is shown before the server confirms it (state/session.svelte.ts `load`): when the server names another,
       every view is mounted again for that one, keeping nothing the first rendered. -->
  <LoadingGate phase={session.status === 'loading' ? 'loading' : 'ready'} view={oneStep('Changeling', 'Your session', session.status === 'loading' ? 'loading' : 'ready')} linger={!session.user}>
  {#key session.user?.id}
  {#if session.status === 'error' && session.failure}
    <Unreachable failure={session.failure} />
  {:else if !session.user}
    <SignIn />
  {:else if route.name === 'compare'}
    {#key subjectKey(route.subject)}
      <PaneBoundary what="this compare"><CompareView {route} /></PaneBoundary>
    {/key}
  {:else if route.name === 'ref'}
    <!-- A locator names its history until it is resolved: two spellings of one ref are two pages, each resolving its own. -->
    {#key `${route.locator.owner}/${route.locator.repo}/${route.locator.spec}${route.locator.path === undefined ? '' : '?' + route.locator.path}`}
      <PaneBoundary what="this history"><RefView {route} /></PaneBoundary>
    {/key}
  {:else if 'subject' in route}
    {#key subjectKey(route.subject)}
      <!-- What the pull request's store works out is PrView's: an error there stops this view, and says so, not silently every later update. -->
      <PaneBoundary what="this pull request"><PrView {route} /></PaneBoundary>
    {/key}
  {:else}
    <div class="shell">
      <Rail />
      <main>
        {#if route.name === 'dashboard'}
          <Dashboard />
        {:else if route.name === 'repos'}
          <RepoIndex />
        {:else if route.name === 'repo'}
          {#key `${route.owner}/${route.repo}`}<RepoPage owner={route.owner} repo={route.repo} tab={route.tab} />{/key}
        {:else if route.name === 'settings'}
          <Settings />
        {:else if route.name === 'org-settings'}
          {#key route.org}<OrgSettings org={route.org} />{/key}
        {:else if route.name === 'feedback'}
          <Feedback />
        {:else if route.name === 'feedback-report'}
          {#key route.id}<Feedback id={route.id} />{/key}
        {:else}
          <div class="center-msg">
            <h1>Not found</h1>
            <p class="muted">Nothing lives at <code>{route.path}</code>.</p>
            <a class="btn" href="/">Go to the dashboard</a>
          </div>
        {/if}
      </main>
    </div>
  {/if}
  {/key}
  </LoadingGate>
  <OperatorFooter />
</div>
{#if session.user}{#key session.user.id}<FeedbackDialog /><JumpBar />{/key}{/if}
<!-- Signed in, this tab serves the user's paired agents and offers their connections for approval (state/relayAgents.svelte.ts). -->
{#if session.user && session.agents}{#key session.user.login}<RelayNotices />{/key}{/if}
